import {
  Box,
  Grid,
  LinearProgress,
  makeStyles,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { signalRConnection } from "utils/signalr";
import { useDataProvider, useNotify } from "react-admin";
import {
  getAssignmentWithVehicleLocation,
  getDataFromJson,
  SchoolIdSelected,
} from "utils/tools";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BusMarker from "./BusMarker";
import SchoolMarker from "./SchoolMarker";
import { cloneDeep, debounce, findIndex } from "lodash";
import moment from "moment";
import RouteDetail from "./RouteDetail";
import LocationMarker from "./LocationMarker";
import {
  colors,
  getExistingRoutePolyline,
} from "../../enhancedRoutePlans/tools";
import ListAltIcon from "@material-ui/icons/ListAlt";

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: "bold",
    color: "#2B3674",
  },
  time: {
    fontWeight: "bold",
    color: "#2B3674",
    alignSelf: "center",
  },
  mapWrapper: {
    height: "70vh",
    width: "100%",
    backgroundColor: "#f2f2f2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    borderRadius: "10px",
    position: "relative",
  },
  formControl: {
    minWidth: 120,
    marginLeft: 16,
  },
}));

const OperationMap = () => {
  const classes = useStyles();
  const selectedSchoolId = SchoolIdSelected();
  const connection = signalRConnection();
  const provider = useDataProvider();

  const [assignmentVehicleLocations, setAssignmentVehicleLocations] = useState<
    IAssignmentWithVehicleLocation[]
  >([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedAssignment, setSelectedAssignment] =
    useState<IAssignment | null>(null);
  const [schoolData, setSchoolData] = useState<ISchool>();
  const [mapState, setMapState] = useState({
    zoom: 12, // Default zoom level
  });
  const [selectedVehicleLocationState, setSelectedVehicleLocationState] =
    useState<any>(null);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [mapsLayoutInstance, setMapsLayout] = useState<any>(null);
  const [polylines, setPolylines] = useState<any>([]);
  const [currentTime, setCurrentTime] = useState(moment());
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const notify = useNotify();

  const debouncedUpdate = useRef(
    debounce((callback: () => Promise<void>) => {
      callback();
    }, 2000), // 2s debounce delay
  ).current;

  const handleApiLoaded = useCallback(
    (map: any, maps: any) => {
      // use map and maps objects
      setMapInstance(map);
      setMapsLayout(maps);
    },
    [setMapInstance, setMapsLayout],
  );

  const getRoutePolylines = async (assignment: any) => {
    try {
      await clearPolyline();

      if (assignment.routeGeometries.length > 0) {
        let allPolylines: any = [];

        for (const routeGeometry of assignment.routeGeometries) {
          const polyline = await getExistingRoutePolyline(
            routeGeometry.polyline,
          );
          allPolylines = allPolylines.concat(polyline);
        }

        await renderPolyline(
          mapInstance,
          mapsLayoutInstance,
          allPolylines,
          colors[0].colorOne,
        );
      }

      if (assignment.tripHistoryUrl) {
        const tripHistoryData = await getDataFromJson(assignment.tripHistoryUrl);

        if (tripHistoryData) {
          let allPolylines: any = [];

          for (const tripHistory of tripHistoryData.json) {
            const polyline = { lat: tripHistory.Lat, lng: tripHistory.Lng };
            allPolylines.push(polyline);
          }

          await renderPolyline(
            mapInstance,
            mapsLayoutInstance,
            allPolylines,
            "#ff5722",
          );
        }
      }
    } catch (error) {
      await notify("Failed to load routeline", "warning");
      console.error(error);
    }
  };

  const renderPolyline = async (
    map: any,
    maps: any,
    paths: any[],
    routeColor: string,
  ) => {
    if (!paths.length) return;

    let geodesicPolyline = new maps.Polyline({
      path: paths,
      geodesic: true,
      strokeColor: routeColor,
      strokeOpacity: 1.0,
      strokeWeight: 5,
      icons: [
        {
          offset: "100%",
          repeat: "50px",
        },
      ],
    });

    geodesicPolyline.setMap(map);

    setPolylines((prev: any) => [...prev, ...[geodesicPolyline]]);
  };

  const clearPolyline = () => {
    if (polylines.length > 0) {
      polylines.forEach((polyline: any) => {
        polyline.setMap(null);
      });

      setPolylines([]);
    }
  };

  const updateAssignmentVehicleLocation = useCallback(
    (incomingVehicleLocationData: any) => {
      const getVehicleLocationUpdate = (
        existingAssignmentVehicleLocationData: any,
        incomingVehicleLocationData: any,
      ) => {
        let existingAssignmentVehicleLocationDataClone: any[] = cloneDeep(
          existingAssignmentVehicleLocationData,
        );

        const indexToUpdate = findIndex(
          existingAssignmentVehicleLocationDataClone,
          (o: any) => {
            return o.id === incomingVehicleLocationData.assignment.id;
          },
        );

        if (indexToUpdate !== -1) {
          existingAssignmentVehicleLocationDataClone[indexToUpdate].vehicle = {
            id: incomingVehicleLocationData.vehicle.id,
            plateNumber: incomingVehicleLocationData.vehicle.plateNumber,
            lat: incomingVehicleLocationData.vehicle.lastLocationLat,
            lng: incomingVehicleLocationData.vehicle.lastLocationLng,
            lastUpdatedAt: moment
              .unix(incomingVehicleLocationData.vehicle.timestamp)
              .toISOString(),
          };

          existingAssignmentVehicleLocationDataClone[indexToUpdate].nextStop =
            incomingVehicleLocationData.nextStop;
        } else {
          const newVehicleLocation = {
            id: incomingVehicleLocationData.assignment.id,
            vehicle: {
              id: incomingVehicleLocationData.vehicle.id,
              plateNumber: incomingVehicleLocationData.vehicle.plateNumber,
              lat: incomingVehicleLocationData.vehicle.lastLocationLat,
              lng: incomingVehicleLocationData.vehicle.lastLocationLng,
              lastUpdatedAt: moment
                .unix(incomingVehicleLocationData.vehicle.timestamp)
                .toISOString(),
            },
            nextStop: incomingVehicleLocationData.nextStop,
          };

          existingAssignmentVehicleLocationDataClone = [
            ...existingAssignmentVehicleLocationDataClone,
            newVehicleLocation,
          ];
        }

        return existingAssignmentVehicleLocationDataClone;
      };

      setAssignmentVehicleLocations((state) =>
        getVehicleLocationUpdate(state, incomingVehicleLocationData),
      );
    },
    [],
  );

  const updateAssignmentVehicleLocationAfterAssignmentStatusUpdate =
    useCallback(async () => {
      const assignments = await getAssignmentWithVehicleLocation(
        provider,
        selectedSchoolId,
      );

      assignments.forEach((assignment: IAssignmentWithVehicleLocation) => {
        if (assignment.vehicle) {
          assignment.vehicle.lastUpdatedAt = moment
            .unix(assignment.vehicle.timestamp)
            .toISOString();
        }
      });

      const incomingIds = new Set(assignments.map((item) => item.id));

      // Get current state snapshot
      const currentState = assignmentVehicleLocations;
      const currentIds = new Set(currentState.map((item) => item.id));

      // Compute diffs
      const removedIds = currentState
        .map((item) => item.id)
        .filter((id) => !incomingIds.has(id));

      const addedIds = assignments
        .map((item) => item.id)
        .filter((id) => !currentIds.has(id));

      // Update state
      const updatedItems = currentState
        .filter((item) => incomingIds.has(item.id))
        .map((item) => {
          const newItem = assignments.find((n) => n.id === item.id);
          return newItem ?? item;
        });

      const newSortedState = assignments.map(
        (inc) => updatedItems.find((u) => u.id === inc.id) ?? inc,
      );

      setAssignmentVehicleLocations(newSortedState);

      try {
        // Leave groups for removed assignments
        for (const id of removedIds) {
          await connection.invoke("LeftGroup", `assignment:${id}`);
        }

        // Join groups for new assignments
        for (const id of addedIds) {
          await connection.invoke("JoinGroup", `assignment:${id}`);
        }
      } catch (error) {
        console.error("SignalR group update failed:", error);
      }
    }, [assignmentVehicleLocations, connection]);

  const startConnection = async (
    onConnected: () => void,
    retryCount = 0,
  ): Promise<void> => {
    try {
      // Only stop if already connected or connecting
      if (connection.state !== "Disconnected") {
        await connection.stop();
      }

      await connection.start();

      // Clean up old handlers to prevent duplicates
      connection.off("BroadcastRealtimeVehicle");
      connection.off("ActiveAssignmentUpdated");

      // Register new handlers
      connection.on("BroadcastRealtimeVehicle", (data: any) => {
        updateAssignmentVehicleLocation(data);
      });

      connection.on("ActiveAssignmentUpdated", () => {
        debouncedUpdate(
          updateAssignmentVehicleLocationAfterAssignmentStatusUpdate,
        );
      });

      // Trigger initial data fetch
      onConnected();
    } catch (err) {
      console.error(
        `SignalR connection attempt ${retryCount + 1} failed:`,
        err,
      );

      if (retryCount < 3) {
        setTimeout(() => {
          startConnection(onConnected, retryCount + 1);
        }, 5000);
      } else {
        console.error("Max SignalR retry attempts reached. Giving up.");
        notify("Unable to connect to real-time updates.", "error");
      }
    }
  };

  const getSelectedSchool = async () => {
    try {
      const response = await provider.getOne("schools", {
        id: Number(selectedSchoolId),
      });
      setSchoolData(response.data as ISchool);
    } catch {
      setSchoolData(null);
    }
  };

  const resetAssignmentVehicleLocations = () => {
    assignmentVehicleLocations.forEach(async (assignment) => {
      try {
        await connection.invoke("LeftGroup", `assignment:${assignment.id}`);
      } catch (err) {
        console.error(`ERROR invoke LEFT GROUP ${assignment.id}`, err);
      }
    });

    setAssignmentVehicleLocations([]);
  };

  const handleMapChange = ({ zoom }: any) => {
    setMapState((prevState) => ({
      ...prevState,
      zoom,
    }));
  };

  const handleOpenAssignmentDetail = (
    route: IAssignmentWithVehicleLocation,
    vehicleLocation: any,
  ) => {
    setSelectedRoute(route);
    setSelectedVehicleLocationState({ vehicle: vehicleLocation, zoom: 14 });
  };

  const handleCloseAssignmentDetail = () => {
    setSelectedRoute(null);
    setSelectedAssignment(null);
    setSelectedVehicleLocationState(null);
  };

  useEffect(() => {
    setSelectedRoute(null);
    setSelectedAssignment(null);
    setSelectedVehicleLocationState(null);

    if (!selectedSchoolId) {
      // Clean up when no school is selected
      connection.off("BroadcastRealtimeVehicle");
      connection.off("ActiveAssignmentUpdated");
      if (connection.state !== "Disconnected") {
        connection.stop().catch(console.error);
      }
      return;
    }

    let currentAssignmentIds: number[] = [];

    const fetchDataAndJoinGroups = async () => {
      setIsLoading(true);
      resetAssignmentVehicleLocations();

      try {
        await getSelectedSchool();

        const assignments = await getAssignmentWithVehicleLocation(
          provider,
          selectedSchoolId,
        );

        // transform to add vehicle last updated time
        assignments.forEach((assignment: IAssignmentWithVehicleLocation) => {
          if (assignment.vehicle) {
            assignment.vehicle.lastUpdatedAt = moment
              .unix(assignment.vehicle.timestamp)
              .toISOString();
          }
        });

        setAssignmentVehicleLocations(assignments);
        currentAssignmentIds = assignments.map((a) => a.id);

        // Join SignalR groups for each assignment
        for (const assignment of assignments) {
          try {
            await connection.invoke("JoinGroup", `assignment:${assignment.id}`);
          } catch (err) {
            console.error(
              `Failed to join group for assignment ${assignment.id}:`,
              err,
            );
          }
        }
      } catch (error) {
        notify("Something went wrong while loading assignments.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    // Rejoin groups when reconnected
    const onReconnected = () => {
      console.log("SignalR reconnected. Rejoining groups...");
      currentAssignmentIds.forEach((id) => {
        connection
          .invoke("JoinGroup", `assignment:${id}`)
          .catch((err: any) =>
            console.error(`Failed to rejoin group ${id}:`, err),
          );
      });
    };

    // Attach reconnection handler
    connection.onreconnected(onReconnected);

    // Start SignalR connection
    startConnection(fetchDataAndJoinGroups);

    // Cleanup on unmount or school change
    return () => {
      connection.off("BroadcastRealtimeVehicle");
      connection.off("ActiveAssignmentUpdated");

      if (connection.state !== "Disconnected") {
        connection.stop().catch(console.error);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  useEffect(() => {
    if (!mapsLayoutInstance || !mapInstance) return;

    if (selectedAssignment) {
      getRoutePolylines(selectedAssignment);
    } else {
      clearPolyline();
    }
  }, [selectedAssignment, mapsLayoutInstance, mapInstance]);

  useEffect(() => {
    if (selectedAssignment) {
      const findAssignment = assignmentVehicleLocations.find(
        (a) => a.id === selectedAssignment.id,
      );

      if (findAssignment && findAssignment.vehicle) {
        const vehicleLocation = {
          lat: findAssignment.vehicle.lat,
          lng: findAssignment.vehicle.lng,
        };

        setSelectedVehicleLocationState({ vehicle: vehicleLocation, zoom: 14 });
      }
    }
  }, [selectedAssignment, assignmentVehicleLocations]);

  useEffect(() => {
    // Update the time every second
    const intervalId = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    // Clear the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, []);

  // Important: Cancel pending debounced calls on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  return (
    <SanitizedGrid container spacing={2}>
      <Grid item xs={12}>
        <Box style={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h4" className={classes.title} gutterBottom>
            Operation Map
          </Typography>
          <Typography variant="h5" className={classes.time} gutterBottom>
            {currentTime.format("DD MMMM, YYYY hh:mm A")}
          </Typography>
        </Box>

        {isLoading ? (
          <LinearProgress />
        ) : (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#2B3674",
                  }}
                >
                  <ListAltIcon
                    fontSize="default"
                    style={{ marginRight: "8px", color: "#05CD99" }}
                  />
                  <Typography variant="body1">
                    Total active trip{" "}
                    <strong>
                      {
                        assignmentVehicleLocations.filter(
                          (item) => item.school != null && item.vehicle != null,
                        ).length
                      }
                    </strong>
                  </Typography>
                </Box>
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#2B3674",
                  }}
                >
                  <CheckCircleIcon
                    fontSize="default"
                    style={{ marginRight: "8px", color: "#05CD99" }}
                  />
                  <Typography variant="body1">
                    Total online bus{" "}
                    <strong>
                      {
                        assignmentVehicleLocations.filter(
                          (item) =>
                            item.school != null &&
                            item.vehicle != null &&
                            item.status !== "Offline",
                        ).length
                      }
                    </strong>
                  </Typography>
                </Box>
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#2B3674",
                  }}
                >
                  <CheckCircleIcon
                    fontSize="default"
                    style={{ marginRight: "8px", color: "#05CD99" }}
                  />
                  <Typography variant="body1">
                    Total bus with ETA{" "}
                    <strong>
                      {
                        assignmentVehicleLocations.filter(
                          (item) =>
                            item.school != null &&
                            item.vehicle != null &&
                            item.nextStop != null,
                        ).length
                      }
                    </strong>
                  </Typography>
                </Box>
              </Typography>
            </Box>

            <Box>
              <FormControl
                variant="outlined"
                className={classes.formControl}
                size="small"
              >
                <InputLabel id="filter-status-label">Filter Bus</InputLabel>
                <Select
                  labelId="filter-status-label"
                  id="filter-status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as string)}
                  label="Filter Bus"
                >
                  <MenuItem value="all">All Bus</MenuItem>
                  <MenuItem value="online">Online Bus</MenuItem>
                  <MenuItem value="offline">Offline Bus</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </Grid>

      <Grid item xs={12}>
        <div className={classes.mapWrapper}>
          <RouteDetail
            selectedRoute={selectedRoute}
            assignmentVehicleLocations={assignmentVehicleLocations}
            handleCloseAssignmentDetail={handleCloseAssignmentDetail}
            setSelectedAssignment={setSelectedAssignment}
          />

          <GoogleMapReact
            bootstrapURLKeys={{
              key: "AIzaSyDYGvy_HvsUfvJ_7_d4PmOH201jIFNn-iM",
            }}
            defaultCenter={{
              lat: 1.415039,
              lng: 103.807996,
            }}
            center={
              selectedVehicleLocationState &&
              selectedVehicleLocationState.vehicle
            }
            defaultZoom={12}
            zoom={
              selectedVehicleLocationState && selectedVehicleLocationState.zoom
            }
            onChange={handleMapChange} // Track map changes
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
          >
            {assignmentVehicleLocations
              .filter((assignment) => {
                if (filterStatus === "all") return true;

                const { vehicle } = assignment;
                if (!vehicle || !vehicle.lastUpdatedAt) return false;

                const lastUpdatedAt = moment(vehicle.lastUpdatedAt);
                const diffSeconds = currentTime.diff(lastUpdatedAt, "seconds");

                if (filterStatus === "online") {
                  return diffSeconds < 30;
                } else if (filterStatus === "offline") {
                  return diffSeconds >= 30;
                }

                return true;
              })
              .map((assignmentWithVehicleLocation, key: number) => {
                // @TODO: Integrate with the API
                // const status = ["Normal", "Breakdown", "Deviation", "Delayed"];
                // assignment.status = status[key % 4];
                assignmentWithVehicleLocation.status = "Normal";

                const { vehicle, school } = assignmentWithVehicleLocation;

                if (!vehicle || !school) {
                  return <></>;
                }

                // mark bus as offline if last updated time is more than 30 seconds
                if (vehicle.lastUpdatedAt) {
                  const lastUpdatedAt = moment(vehicle.lastUpdatedAt);

                  if (currentTime.diff(lastUpdatedAt, "seconds") > 30) {
                    assignmentWithVehicleLocation.status = "Offline";
                  }
                }

                const vehicleLocation = { lat: vehicle.lat, lng: vehicle.lng };

                return (
                  <BusMarker
                    key={`bus-marker-${key}`}
                    assignmentVehicleLocation={assignmentWithVehicleLocation}
                    school={schoolData}
                    lat={vehicle.lat}
                    lng={vehicle.lng}
                    zoom={mapState.zoom}
                    onClick={() =>
                      handleOpenAssignmentDetail(
                        assignmentWithVehicleLocation,
                        vehicleLocation,
                      )
                    }
                  />
                );
              })}

            {selectedAssignment &&
              selectedAssignment.locations.map((location, key: number) => {
                const allPassengersHaveNotTaking =
                  location.passengers.length > 0 &&
                  location.passengers.every((p) =>
                    p.logs.some(
                      (log) =>
                        log.type === "not_taking" ||
                        log.type === "exclude_student",
                    ),
                  );

                return (
                  <LocationMarker
                    lat={location.lat}
                    lng={location.lng}
                    key={`location-marker${key}`}
                    zoom={mapState.zoom}
                    order={
                      selectedAssignment.routeType === "to_school"
                        ? location.order
                        : location.order - 1
                    }
                    data={{
                      ...location,
                      routeName: selectedAssignment.routeName,
                    }}
                    colorOne={
                      allPassengersHaveNotTaking ? "#F87171" : "#78B9FF"
                    }
                    colorTwo={
                      allPassengersHaveNotTaking ? "#EF4444" : "#127EF1"
                    }
                  />
                );
              })}

            {schoolData && (
              <SchoolMarker
                key={`school-marker`}
                lat={schoolData.lat}
                lng={schoolData.lng}
                record={schoolData}
                zoom={mapState.zoom}
              />
            )}
          </GoogleMapReact>
        </div>

        {selectedAssignment && (
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              marginTop: "20px",
              marginLeft: "20px",
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box
                style={{
                  width: "24px",
                  height: "4px",
                  backgroundColor: colors[0].colorOne,
                  borderRadius: "2px",
                }}
              />
              <Typography variant="body2" style={{ color: "#2B3674" }}>
                Planned Routes
              </Typography>
            </Box>
            <Box style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box
                style={{
                  width: "24px",
                  height: "4px",
                  backgroundColor: "#ff5722",
                  borderRadius: "2px",
                }}
              />
              <Typography variant="body2" style={{ color: "#2B3674" }}>
                Real Routes
              </Typography>
            </Box>
          </Box>
        )}
      </Grid>
    </SanitizedGrid>
  );
};

export default memo(OperationMap);
